/**
 * Cron 表达式解析
 *
 * 支持 5 段（分 时 日 月 周）与 6 段（秒 分 时 日 月 周，秒段会被忽略）。
 * 采用逐分钟推演的方式求下一次执行时间，实现简单且不易出错，
 * 代价是最多只向后搜索四年，找不到就认为表达式不会再触发。
 */

const FIELD_RANGES = [
	{ name: '分钟', min: 0, max: 59 },
	{ name: '小时', min: 0, max: 23 },
	{ name: '日', min: 1, max: 31 },
	{ name: '月', min: 1, max: 12 },
	{ name: '星期', min: 0, max: 7 }
];

const WEEK_NAMES = ['日', '一', '二', '三', '四', '五', '六'];

/**
 * 把一个字段展开成允许的取值集合
 */
function parseField(expr, range) {
	const allowed = new Set();

	for (const part of expr.split(',')) {
		const [rangePart, stepPart] = part.split('/');
		const step = stepPart ? parseInt(stepPart, 10) : 1;
		if (!step || step < 1) {
			throw new Error(`${range.name} 的步长无效：${part}`);
		}

		let start;
		let end;
		if (rangePart === '*' || rangePart === '?') {
			start = range.min;
			end = range.max;
		} else if (rangePart.indexOf('-') > -1) {
			const [from, to] = rangePart.split('-');
			start = parseInt(from, 10);
			end = parseInt(to, 10);
		} else {
			start = parseInt(rangePart, 10);
			end = stepPart ? range.max : start;
		}

		if (isNaN(start) || isNaN(end)) {
			throw new Error(`${range.name} 无法识别：${part}`);
		}
		if (start < range.min || end > range.max || start > end) {
			throw new Error(`${range.name} 超出取值范围 ${range.min}-${range.max}`);
		}

		for (let value = start; value <= end; value += step) {
			allowed.add(value);
		}
	}

	return allowed;
}

/**
 * 解析表达式，返回各字段允许的取值
 */
export function parseCron(expression) {
	const parts = String(expression || '').trim().split(/\s+/).filter(Boolean);
	if (parts.length !== 5 && parts.length !== 6) {
		throw new Error('表达式需要 5 段或 6 段，当前是 ' + parts.length + ' 段');
	}

	// 六段格式的第一段是秒，这里按分钟粒度推演，忽略秒
	const fields = parts.length === 6 ? parts.slice(1) : parts;

	const sets = fields.map((field, index) => parseField(field, FIELD_RANGES[index]));

	// 星期允许写 7 表示周日，统一成 0
	if (sets[4].has(7)) {
		sets[4].delete(7);
		sets[4].add(0);
	}

	return {
		minutes: sets[0],
		hours: sets[1],
		days: sets[2],
		months: sets[3],
		weekdays: sets[4],
		// 日和周都写了具体值时，Cron 的语义是满足其一即可
		dayRestricted: fields[2] !== '*' && fields[2] !== '?',
		weekRestricted: fields[4] !== '*' && fields[4] !== '?'
	};
}

/**
 * 推算接下来的若干次执行时间
 */
export function nextRunTimes(expression, count = 5, from = new Date()) {
	const cron = parseCron(expression);
	const results = [];

	const cursor = new Date(from.getTime());
	cursor.setSeconds(0, 0);
	cursor.setMinutes(cursor.getMinutes() + 1);

	// 四年足以覆盖闰年带来的 2 月 29 日
	const maxIterations = 60 * 24 * 366 * 4;
	for (let i = 0; i < maxIterations && results.length < count; i++) {
		if (matches(cron, cursor)) {
			results.push(formatDate(cursor));
		}
		cursor.setMinutes(cursor.getMinutes() + 1);
	}

	return results;
}

function matches(cron, date) {
	if (!cron.minutes.has(date.getMinutes())) return false;
	if (!cron.hours.has(date.getHours())) return false;
	if (!cron.months.has(date.getMonth() + 1)) return false;

	const dayHit = cron.days.has(date.getDate());
	const weekHit = cron.weekdays.has(date.getDay());

	if (cron.dayRestricted && cron.weekRestricted) {
		return dayHit || weekHit;
	}
	if (cron.dayRestricted) {
		return dayHit;
	}
	if (cron.weekRestricted) {
		return weekHit;
	}
	return true;
}

/**
 * 生成一段人类可读的描述
 */
export function describeCron(expression) {
	const cron = parseCron(expression);
	const lines = [];
	lines.push('分钟：' + describeSet(cron.minutes, 0, 59));
	lines.push('小时：' + describeSet(cron.hours, 0, 23));
	lines.push('日期：' + (cron.dayRestricted ? describeSet(cron.days, 1, 31) : '每天'));
	lines.push('月份：' + describeSet(cron.months, 1, 12));
	lines.push('星期：' + (cron.weekRestricted
		? Array.from(cron.weekdays).sort((a, b) => a - b).map(d => '周' + WEEK_NAMES[d]).join('、')
		: '不限'));
	return lines.join('\n');
}

function describeSet(set, min, max) {
	if (set.size === max - min + 1) {
		return '每个值';
	}
	const values = Array.from(set).sort((a, b) => a - b);
	if (values.length > 12) {
		return values.slice(0, 12).join(', ') + ' 等 ' + values.length + ' 个值';
	}
	return values.join(', ');
}

function formatDate(date) {
	const pad = (n) => String(n).padStart(2, '0');
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} `
		+ `${pad(date.getHours())}:${pad(date.getMinutes())}:00 周${WEEK_NAMES[date.getDay()]}`;
}
